import { useNavigate } from "react-router-dom";

interface Props {
  title?: string;
  message?: string;
  onRetry?: () => void;
  /** Show "Go to Login" button — use for auth errors */
  showLogin?: boolean;
  /** Wrap in full-screen container — use when outside any layout */
  fullPage?: boolean;
  /** "dark" = admin/portal, "light" = public page. Default: dark */
  theme?: "dark" | "light";
}

export default function ErrorState({
  title   = "Something went wrong",
  message = "We couldn\u2019t load this page. Please check your connection and try again.",
  onRetry,
  showLogin = false,
  fullPage  = false,
  theme     = "dark",
}: Props) {
  const navigate = useNavigate();

  const bgClass        = theme === "light" ? "bg-white"         : "bg-[#f3f7fb]";
  const cardBg         = theme === "light" ? "bg-[#fafafa] border-slate-200"  : "bg-white border-slate-200";
  const titleColor     = theme === "light" ? "text-[#0f172a]"   : "text-[#0f172a]";
  const msgColor       = theme === "light" ? "text-[#0f172a]/50" : "text-slate-500";
  const retryBtnClass  = "bg-[#C8A96E] hover:bg-[#b8965a] text-black";
  const loginBtnClass  = theme === "light"
    ? "bg-slate-100 hover:bg-slate-200 text-slate-600"
    : "bg-slate-100 hover:bg-slate-200 text-slate-600";

  const inner = (
    <div className="flex flex-col items-center justify-center py-24 px-6">
      <div className={`max-w-sm w-full rounded-2xl border p-8 text-center ${cardBg}`}>
        {/* Icon */}
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-400/10 mx-auto mb-5">
          <i className="ri-error-warning-line text-red-400 text-2xl"></i>
        </div>

        {/* Text */}
        <h3 className={`font-semibold text-base mb-2 ${titleColor}`}>{title}</h3>
        <p className={`text-sm leading-relaxed mb-6 ${msgColor}`}>{message}</p>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {onRetry && (
            <button
              onClick={onRetry}
              className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${retryBtnClass}`}
            >
              <i className="ri-refresh-line"></i>
              Try Again
            </button>
          )}
          {showLogin && (
            <button
              onClick={() => navigate("/login")}
              className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${loginBtnClass}`}
            >
              <i className="ri-login-circle-line"></i>
              Go to Login
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (fullPage) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center px-6`}>
        <div className={`max-w-sm w-full rounded-2xl border p-8 text-center ${cardBg}`}>
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-400/10 mx-auto mb-5">
            <i className="ri-error-warning-line text-red-400 text-2xl"></i>
          </div>
          <h3 className={`font-semibold text-base mb-2 ${titleColor}`}>{title}</h3>
          <p className={`text-sm leading-relaxed mb-6 ${msgColor}`}>{message}</p>
          <div className="flex flex-col gap-2">
            {onRetry && (
              <button
                onClick={onRetry}
                className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${retryBtnClass}`}
              >
                <i className="ri-refresh-line"></i>
                Try Again
              </button>
            )}
            {showLogin && (
              <button
                onClick={() => navigate("/login")}
                className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${loginBtnClass}`}
              >
                <i className="ri-login-circle-line"></i>
                Go to Login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return inner;
}
